import { Alert, Box } from '@mui/material';
import { toNumber } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { getActiveEmployeeDropdownItems } from 'store/actions/employee-actions';
import { getLocationsDropdownItemsAction } from 'store/actions/company-locations-actions';
import { getMotiveExceptionsAction } from 'store/actions/motive-exception-actions';
import { getPayrollAction } from 'store/actions/payroll-actions';
import {
  deleteMotiveExceptionErrorSelector,
  motiveExceptionsSelector,
} from 'store/selectors/motive-exception-selectors';
import {
  GENERAL__ERROR_MESSAGE,
  MOTIVE_EXCEPTION__ADD_BUTTON,
  MOTIVE_EXCEPTION__EMPTY_STATE,
} from 'translations/constants';
import IMotiveException from 'types/company/payroll/IMotiveException';
import { AddMotiveExceptionButton } from './styles';
import MotiveExceptionModal from './MotiveExceptionModal';
import MotiveExceptionsGrid from './MotiveExceptionsGrid';

const MotiveExceptions = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();

  const motiveExceptions = useAppSelector(motiveExceptionsSelector);
  const deleteError = useAppSelector(deleteMotiveExceptionErrorSelector);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [motiveExceptionToEdit, setMotiveExceptionToEdit] = useState<IMotiveException | undefined>(undefined);

  useEffect(() => {
    if (companyId) {
      const id = toNumber(companyId);
      dispatch(getMotiveExceptionsAction(id));
      dispatch(getActiveEmployeeDropdownItems({ companyId: id }));
      dispatch(getLocationsDropdownItemsAction({ companyId: id }));
      dispatch(getPayrollAction(id));
    }
  }, [companyId, dispatch]);

  const onAdd = () => {
    setMotiveExceptionToEdit(undefined);
    setOpenModal(true);
  };

  const onEdit = (motiveException: IMotiveException) => {
    setMotiveExceptionToEdit(motiveException);
    setOpenModal(true);
  };

  const onCloseModal = () => {
    setOpenModal(false);
    setMotiveExceptionToEdit(undefined);
  };

  return (
    <Box>
      <AddMotiveExceptionButton text={`+ ${t(MOTIVE_EXCEPTION__ADD_BUTTON)}`} onClick={onAdd} />

      {motiveExceptions.length > 0 ? (
        <MotiveExceptionsGrid motiveExceptions={motiveExceptions} onEdit={onEdit} />
      ) : (
        <Box sx={{ marginTop: '16px' }}>{t(MOTIVE_EXCEPTION__EMPTY_STATE)}</Box>
      )}

      {deleteError && (
        <Box sx={{ marginTop: '16px' }}>
          <Alert severity="error" icon={false} variant="filled" sx={{ width: '800px' }}>
            {t(GENERAL__ERROR_MESSAGE)}
          </Alert>
        </Box>
      )}

      {openModal && <MotiveExceptionModal onClose={onCloseModal} motiveExceptionToEdit={motiveExceptionToEdit} />}
    </Box>
  );
};

export default MotiveExceptions;
